/**
 * BottomBar — mobile (< lg) chrome that replaces the desktop SideBarMenu.
 *
 * A mobile presentation of the SAME nav model and access rules as
 * `components/common/SideBarMenu.tsx`: 4 primary tabs (Dashboard, Revenue,
 * Order, Alerts) + a "More" drawer holding every other page, each gated by the
 * exact same access-control helpers the sidebar uses. Rendered by App.tsx under
 * the same guard as NavBar/SideBarMenu and gated `lg:hidden`, so desktop (>= lg)
 * is untouched.
 *
 * Usage: `{user && isActive && !isSpecificEndpoint && <BottomBar />}`
 */
import { getOperationalPages, hasMainPageRestriction, STAFF_HOME_PATH } from '../../../services/accessManagement/mainPageRestriction';
import { useAnalysisStore } from '../../../stores/appAnalysisStore';
import { useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home, DollarSign, Clock8, Megaphone, MoreHorizontal, Calculator, BookOpen,
  BarChart3, Percent, Trash2, FileText, TrendingUp, ChefHat, ClipboardCheck,
  UtensilsCrossed, Package, Bell, IdCardLanyard, Settings, LogOut,
  ChevronRight, ChevronDown, Shield, Target,
} from 'lucide-react';
import { useAuth } from '../../../services/authentication/authContext';
import { signOut as supabaseSignOut } from '../../../services/authentication/supabaseService';
import {
  isInvoiceOnlyAccount, canAccessRecipesPage, canAccessChecklistsPage,
  canAccessMepPage, canAccessNotificationsPage, canAccessWastePage,
  canAccessStaffPage, canAccessInventoryPage, canAccessReportsPage,
  canAccessCostPercentPage, canAccessAnalysisPage,
  canAccessSuperAdminDashboard,
} from '../../../services/accessManagement/accessManagement';
import { ImpersonationSelector } from '../../common/ImpersonationControls';
import { Collapse } from '../../common/Collapse';
import ThemeToggle from '../../common/ThemeToggle';
import LanguageToggle from '../../common/LanguageToggle';
import { cardBg, borderGrey, primaryText } from '../../../styling/stylingHelper';
import { cn } from '../../../lib/utils';
import { SupportNavLink, SupportUnreadBadge } from '../../common/SupportNavLink';
import { SetupOverviewNavLink } from '../../common/SetupOverviewNavLink';

const BottomBar: React.FC = () => {
  const { t } = useTranslation();
  const hasUnseenAnalysis = useAnalysisStore(s => !s.analysisFindingsSeen && Object.values(s.wasteFindings).some(findings => findings.length > 0));
  const navigate = useNavigate();
  const { user, backendUser, accountSettings } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [appPagesOpen, setAppPagesOpen] = useState(false);
  const [moreSecOpen, setMoreSecOpen] = useState(false);

  const userInitial = user?.email?.charAt(0).toUpperCase() || '';

  // Same class strings SideBarMenu uses for its links — one visual language.
  const drawerItemClass = 'text-tertiary dark:text-tertiary-dark flex py-2 items-center gap-2 p-2 rounded-xl font-medium hover:text-textGreen dark:hover:text-text-dark hover:bg-cardLightGreen/80 dark:hover:bg-cardLightGreen-dark/80';
  const drawerItemActiveClass = 'bg-cardLightGreen dark:bg-cardLightGreen-dark text-textGreen dark:text-text-dark';
  const sectionLabelClass = 'w-full flex items-center gap-1 px-2 py-1 text-xs font-semibold uppercase tracking-wider text-muted dark:text-muted-dark hover:text-tertiary dark:hover:text-tertiary-dark';

  const handleLogout = async () => {
    try {
      await supabaseSignOut();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout error', err);
    }
  };

  const closeMore = () => setMoreOpen(false);

  if (!backendUser) return null;

  // Invoice-only accounts get a deliberately minimal nav — 3 tabs plus Settings +
  // Logout in the sheet. It OVERRIDES every other account setting (including the
  // ones forced on while impersonating), so every gate below is forced off.
  const restricted = hasMainPageRestriction(backendUser);
  const invoiceOnly = !restricted && isInvoiceOnlyAccount(backendUser, accountSettings);

  // Access gating — mirrors SideBarMenu exactly.
  const hasRecipesAccess = !invoiceOnly && canAccessRecipesPage(backendUser, accountSettings);
  const hasChecklistsAccess = !invoiceOnly && canAccessChecklistsPage(backendUser, accountSettings);
  const hasMepAccess = !invoiceOnly && canAccessMepPage(backendUser, accountSettings);
  const hasNotificationsAccess = !invoiceOnly && canAccessNotificationsPage(backendUser, accountSettings);
  const hasWasteAccess = !invoiceOnly && canAccessWastePage(backendUser, accountSettings);
  const hasInventoryAccess = !invoiceOnly && canAccessInventoryPage(backendUser, accountSettings);
  const hasReportsAccess = !restricted && !invoiceOnly && canAccessReportsPage(backendUser, accountSettings);
  const hasCostPercentAccess = !restricted && !invoiceOnly && canAccessCostPercentPage(backendUser, accountSettings);
  const hasAnalysisAccess = !restricted && !invoiceOnly && canAccessAnalysisPage(backendUser, accountSettings);
  const hasStaffAccess = !invoiceOnly && canAccessStaffPage(backendUser);

  const hasAppPages = hasRecipesAccess || hasChecklistsAccess || hasMepAccess || hasNotificationsAccess || hasWasteAccess || hasInventoryAccess;

  const operationalPages = getOperationalPages(backendUser, accountSettings);
  const operationalIcons: Record<string, typeof Home> = { '/recipes': ChefHat, '/inventory': Package, '/mep': UtensilsCrossed, '/checklists': ClipboardCheck, '/notifications': Bell, '/waste': Trash2, '/staff': IdCardLanyard };
  const tabs = restricted ? (operationalPages.length ? operationalPages.slice(0, 4) : [{ path: STAFF_HOME_PATH, label: 'nav.homePage' }]).map(page => ({ to: page.path, label: t(page.label), Icon: operationalIcons[page.path] ?? Home })) : invoiceOnly ? [
    { to: '/home', label: t('nav.homePage'), Icon: Home },
    { to: '/invoices', label: t('nav.upload', { defaultValue: 'Upload' }), Icon: FileText },
    { to: '/orderhistory/orders', label: t('nav.orderHistory'), Icon: Clock8 },
  ] : [
    { to: '/dashboard', label: t('nav.home'), Icon: Home },
    { to: '/revenuehistory', label: t('nav.revenueHistory'), Icon: DollarSign },
    { to: '/orderhistory', label: t('nav.orderHistory'), Icon: Clock8 },
    { to: '/pricealerts', label: t('nav.priceAlerts'), Icon: Megaphone },
  ];

  const drawerLinkClass = ({ isActive }: { isActive: boolean }) =>
    `${drawerItemClass} ${isActive ? drawerItemActiveClass : ''}`;

  return (
    <>
      {/* Backdrop */}
      {moreOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 dark:bg-black/40 lg:hidden" onClick={closeMore} />
      )}

      {/* More drawer — bottom sheet sitting above the bar */}
      {moreOpen && (
        <div className={cn('fixed bottom-[68px] left-0 right-0 z-50 lg:hidden max-h-[70vh] overflow-y-auto rounded-t-2xl border-t p-3', cardBg, borderGrey)}>
          <div className="flex flex-col space-y-1">
            {/* Super-admin row — mirrors the NavBar desktop controls (/admin link +
                impersonation picker). ImpersonationSelector self-gates (hidden for
                non-super-admins and while impersonating), same as in NavBar.
                Deliberately NOT hidden in invoice-only mode: a super admin previewing
                the mode on their own account needs /admin to remove the setting. */}
            {canAccessSuperAdminDashboard(backendUser) && (
              <div className="flex items-center gap-2">
                <NavLink to="/admin" className={({ isActive }) => `${drawerLinkClass({ isActive })} flex-1`} onClick={closeMore}>
                  <Shield className="w-6 h-6" />
                  {t('nav.admin')}
                </NavLink>
                <ImpersonationSelector />
              </div>
            )}
            {/* Direct items (mirror SideBarMenu top section, minus the 4 tabs) */}
            <SetupOverviewNavLink className={drawerItemClass} activeClassName={drawerItemActiveClass} onClick={closeMore} />
            <SupportNavLink className={drawerItemClass} activeClassName={drawerItemActiveClass} onClick={closeMore} />
            {hasCostPercentAccess && (
              <NavLink to="/costpercent" className={drawerLinkClass} onClick={closeMore}>
                <Percent className="w-6 h-6" />
                {t('nav.costPercent')}
              </NavLink>
            )}
            {hasAnalysisAccess && (
              <NavLink to="/analysis" className={drawerLinkClass} onClick={closeMore}>
                <Target className="w-6 h-6" />
                {t('analysis.navLabel')}
                {hasUnseenAnalysis && <span className="ml-auto rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-xs font-semibold min-w-5 h-5 px-1 flex items-center justify-center">1</span>}
              </NavLink>
            )}
            {!restricted && !invoiceOnly && (
              <NavLink to="/costprice" className={drawerLinkClass} onClick={closeMore}>
                <Calculator className="w-6 h-6" />
                {t('nav.costPrice')}
              </NavLink>
            )}
            {!restricted && !invoiceOnly && (
              <NavLink to="/utilization" className={drawerLinkClass} onClick={closeMore}>
                <Trash2 className="w-6 h-6" />
                {t('nav.productUtilization')}
              </NavLink>
            )}
            {!restricted && !invoiceOnly && (
              <NavLink to="/menus" className={drawerLinkClass} onClick={closeMore}>
                <BookOpen className="w-6 h-6" />
                {t('nav.menus')}
              </NavLink>
            )}
            {!restricted && !invoiceOnly && (
              <NavLink to="/charts" className={drawerLinkClass} onClick={closeMore}>
                <BarChart3 className="w-6 h-6" />
                {t('nav.charts')}
              </NavLink>
            )}
            {hasReportsAccess && (
              <NavLink to="/reports" className={drawerLinkClass} onClick={closeMore}>
                <TrendingUp className="w-6 h-6" />
                {t('nav.reports')}
              </NavLink>
            )}

            {/* App Pages collapsible (gated) */}
            {hasAppPages && (
              <div className="mt-2">
                <button className={sectionLabelClass} onClick={() => setAppPagesOpen(p => !p)}>
                  {t('nav.appPages')}
                  {appPagesOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <Collapse open={appPagesOpen}>
                  <div className="flex flex-col space-y-1 pl-4 mt-1">
                    {hasRecipesAccess && (
                      <NavLink to="/recipes" className={drawerLinkClass} onClick={closeMore}>
                        <ChefHat className="w-6 h-6" />
                        {t('nav.recipes')}
                      </NavLink>
                    )}
                    {hasChecklistsAccess && (
                      <NavLink to="/checklists" className={drawerLinkClass} onClick={closeMore}>
                        <ClipboardCheck className="w-6 h-6" />
                        {t('nav.checklists')}
                      </NavLink>
                    )}
                    {hasMepAccess && (
                      <NavLink to="/mep" className={drawerLinkClass} onClick={closeMore}>
                        <UtensilsCrossed className="w-6 h-6" />
                        {t('nav.mep')}
                      </NavLink>
                    )}
                    {hasInventoryAccess && (
                      <NavLink to="/inventory" className={drawerLinkClass} onClick={closeMore}>
                        <Package className="w-6 h-6" />
                        {t('nav.inventory')}
                      </NavLink>
                    )}
                    {hasNotificationsAccess && (
                      <NavLink to="/notifications" className={drawerLinkClass} onClick={closeMore}>
                        <Bell className="w-6 h-6" />
                        {t('nav.notifications')}
                      </NavLink>
                    )}
                    {hasWasteAccess && (
                      <NavLink to="/waste" className={drawerLinkClass} onClick={closeMore}>
                        <Trash2 className="w-6 h-6" />
                        {t('nav.wasteTracking')}
                      </NavLink>
                    )}
                  </div>
                </Collapse>
              </div>
            )}

            {/* More collapsible (Staff, Settings, Logout). For invoice-only accounts
                this section IS the whole sheet, so it stays open without a toggle. */}
            <div className="mt-2">
              {!invoiceOnly && (
                <button className={sectionLabelClass} onClick={() => setMoreSecOpen(p => !p)}>
                  {t('nav.more')}
                  {moreSecOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              )}
              <Collapse open={moreSecOpen || invoiceOnly}>
                <div className="flex flex-col space-y-1 pl-4 mt-1">
                  {hasStaffAccess && (
                    <NavLink to="/staff" className={drawerLinkClass} onClick={closeMore}>
                      <IdCardLanyard className="w-6 h-6" />
                      {t('nav.staff')}
                    </NavLink>
                  )}
                  {backendUser.role === 'admin' && (
                    <NavLink to="/settings" className={drawerLinkClass} onClick={closeMore}>
                      <Settings className="w-6 h-6" />
                      {t('nav.settings')}
                    </NavLink>
                  )}
                  <button onClick={() => { handleLogout(); closeMore(); }} className={`${drawerItemClass} w-full text-left`}>
                    <LogOut className="w-6 h-6" />
                    {t('nav.logout')}
                  </button>
                </div>
              </Collapse>
            </div>

            {/* Theme + language + profile */}
            <div className="flex items-center gap-3 px-2 pt-2 mt-2 border-t border-borderGrey dark:border-borderGrey-dark">
              <LanguageToggle />
              <ThemeToggle />
              <Link to="/profile" onClick={closeMore} className="ml-auto">
                {backendUser.profile_picture_url ? (
                  <img
                    src={backendUser.profile_picture_url}
                    alt="Profile"
                    className="h-10 w-10 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-400 dark:bg-gray-500 flex items-center justify-center text-white dark:text-white uppercase text-sm">
                    {userInitial}
                  </div>
                )}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Bottom bar */}
      <nav className={cn('lg:hidden fixed bottom-0 left-0 right-0 z-40 flex items-stretch justify-around border-t px-1 py-2', cardBg, borderGrey, primaryText)}>
        {tabs.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={closeMore}
            className={({ isActive }) =>
              cn('flex flex-col items-center justify-center gap-1 flex-1 py-1', isActive ? 'text-textGreen dark:text-text-dark' : 'text-tertiary dark:text-tertiary-dark')
            }
          >
            <Icon className="w-6 h-6" />
            <span className="text-[11px] font-medium">{label}</span>
          </NavLink>
        ))}
        <button
          onClick={() => setMoreOpen(p => !p)}
          className={cn('flex flex-col items-center justify-center gap-1 flex-1 py-1', moreOpen ? 'text-textGreen dark:text-text-dark' : 'text-tertiary dark:text-tertiary-dark')}
        >
          <span className="flex items-center gap-1"><MoreHorizontal className="w-6 h-6" /><SupportUnreadBadge /></span>
          <span className="text-[11px] font-medium">{t('nav.more')}</span>
        </button>
      </nav>
    </>
  );
};

export default BottomBar;
